import { tokens } from "@blurb/codex-react";
import { createStyles, Drawer, DrawerProps, Loader, ScrollArea, useMantineTheme, Text } from "@mantine/core";
import { isGraphQLError } from "@rpi/openapi-api";
import { Flex, IconV2, RpiText } from "@rpi/openapi-core";
import React from "react";
import { createPortal } from "react-dom";
import { optObj } from "../../utils/misc.util";
import { RpaButtonLink } from "../../components/RpiLink";
import { IconV3 } from "@rpi/openapi-core";
import { drawerCloseAnimationMs } from "./drawerAnimation";

export interface RpaDrawerProps extends Omit<DrawerProps, "classNames" | "id"> {
  isLoading?: boolean;
  error?: any;
  overlay?: Omit<RpaDrawerOverlayProps, "overlay">;
  /**
   * Mutes the header close control and stops Escape and overlay clicks from firing `onClose`,
   * for work that shouldn't be interrupted partway through.
   */
  closeDisabled?: boolean;
}

type RpaDrawerContextType = {
  footerContainer: HTMLDivElement | null;
  headerContentContainer: HTMLDivElement | null;
};

const RpaDrawerContext = React.createContext<RpaDrawerContextType>({
  footerContainer: null,
  headerContentContainer: null,
});

const useRpaDrawerStyles = createStyles((theme) => {
  return {
    overlay: {
      opacity: `1 !important`,
      backgroundColor: "rgba(92, 92, 92, 0.67) !important",
      flex: 1,
      overflowY: "auto",
      gap: `${theme.spacing.md}px !important`,
    },
    drawer: {
      width: 362,
      padding: `0px`,
      display: "flex",
      flexDirection: "column",
    },
    header: {
      alignItems: "flex-start",
      padding: `${theme.spacing.lg}px`,
      paddingBottom: tokens.spacing[6],
      width: "100%",
      margin: 0,
      borderBottom: `1px solid ${tokens.color.semantic.border.faint}`,
    },
    title: {
      fontWeight: theme.other.fontWeights.semibold,
      fontSize: theme.other.fontTypes.h3.fontSize,
      lineHeight: theme.other.fontTypes.h3.lineHeight,
      marginRight: 0,
      width: "100%",
      textAlign: "left",
      flex: 1,
    },
    bodyScrollArea: {
      flex: 1,
      minHeight: 0,
    },
    bodyContent: {
      gap: tokens.spacing[2],
      padding: `${tokens.spacing[6]} 0`,
    },
    footer: {
      flex: "0 0 auto",
      borderTop: `1px solid ${tokens.color.semantic.border.faint}`,
      "&:empty": {
        display: "none",
      },
    },
    portalTarget: {
      "&:empty": {
        display: "none",
      },
    },
  };
});

export function RpaDrawer({
  title,
  onClose,
  isLoading,
  error,
  overlay,
  closeDisabled,
  children,
  ...props
}: RpaDrawerProps) {
  const { classes } = useRpaDrawerStyles();
  const theme = useMantineTheme();
  const [footerContainer, setFooterContainer] = React.useState<HTMLDivElement | null>(null);
  const [headerContentContainer, setHeaderContentContainer] = React.useState<HTMLDivElement | null>(null);

  const childrenRender = React.useMemo(() => {
    if (overlay) return;

    if (error) {
      return (
        <RpaDrawerOverlay
          overlay={false}
          type="error"
          message={isGraphQLError(error) ? error.message : "Failed to load."}
        />
      );
    }

    if (isLoading) {
      return <RpaDrawerOverlay overlay={false} type="loading" />;
    }

    return children;
  }, [isLoading, error, overlay, children]);

  return (
    <RpaDrawerContext.Provider value={{ footerContainer, headerContentContainer }}>
      <Drawer
        classNames={classes}
        position="right"
        withCloseButton={false}
        transitionDuration={drawerCloseAnimationMs}
        closeOnEscape={!closeDisabled}
        closeOnClickOutside={!closeDisabled}
        onClose={onClose}
        title={
          <Flex.Column fullWidth gap={tokens.spacing[4]}>
            <Flex.Row fullWidth justify="space-between" align="flex-start">
              <Text>{title || ""}</Text>
              <RpaButtonLink aria-label="Close" data-testid="drawer-close" disabled={closeDisabled} onClick={onClose}>
                {/* IconV3 colors itself inline, so the link's disabled rule can't reach it. */}
                <IconV3
                  icon="close"
                  color={closeDisabled ? tokens.color.semantic.icon.disabled : theme.other.semanticColors.textIconBold}
                />
              </RpaButtonLink>
            </Flex.Row>
            <div ref={setHeaderContentContainer} className={classes.portalTarget} />
          </Flex.Column>
        }
        {...props}
      >
        {!overlay && (
          <>
            <ScrollArea
              className={classes.bodyScrollArea}
              styles={{
                thumb: { backgroundColor: tokens.color.semantic.bg.graySubtle },
                scrollbar: { "&:hover": { backgroundColor: tokens.color.semantic.bg.graySubtlerHover } },
              }}
            >
              <Flex.Column className={classes.bodyContent} children={childrenRender} />
            </ScrollArea>
            <div ref={setFooterContainer} className={classes.footer} />
          </>
        )}
        {overlay && <RpaDrawerOverlay overlay={true} {...overlay} />}
      </Drawer>
    </RpaDrawerContext.Provider>
  );
}

// The drawer container already owns the body's vertical padding, so the body carries only the
// horizontal half; the footer container has none of its own, so its actions pad all four sides.
const useRpaDrawerLayoutStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footerActions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacing[2],
    padding: tokens.spacing[6],
    '&[data-align="stretch"]': {
      alignItems: "stretch",
    },
    '&[data-gap="3"]': {
      gap: tokens.spacing[3],
    },
  },
});

interface RpaDrawerFooterActionsLayout {
  /** "stretch" makes each action span the footer; "center" leaves them at their intrinsic width. */
  align: "center" | "stretch";
  /** Space between stacked actions, as a codex spacing step. */
  gap: 2 | 3;
}

const RpaDrawerBody: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { classes } = useRpaDrawerLayoutStyles();
  return <div className={classes.body}>{children}</div>;
};

/** Stacked primary/secondary actions for a drawer footer. */
const RpaDrawerFooterActions: React.FC<Partial<RpaDrawerFooterActionsLayout> & { children: React.ReactNode }> = ({
  children,
  align = "center",
  gap = 2,
}) => {
  const { classes } = useRpaDrawerLayoutStyles();
  return (
    <div className={classes.footerActions} data-align={align} data-gap={gap} data-testid="drawer-footer-actions">
      {children}
    </div>
  );
};

const RpaDrawerHeaderContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { headerContentContainer } = React.useContext(RpaDrawerContext);
  if (!headerContentContainer) return null;
  return createPortal(children, headerContentContainer);
};

const RpaDrawerFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { footerContainer } = React.useContext(RpaDrawerContext);
  if (!footerContainer) return null;
  return createPortal(children, footerContainer);
};

RpaDrawer.HeaderContent = RpaDrawerHeaderContent;
RpaDrawer.Footer = RpaDrawerFooter;
RpaDrawer.Body = RpaDrawerBody;
RpaDrawer.FooterActions = RpaDrawerFooterActions;

export interface RpaDrawerOverlayProps {
  type: "loading" | "success" | "error";
  message?: string;
  description?: string;
  overlay?: boolean;
  "data-testid"?: string;
}

export const RpaDrawerOverlay: React.FC<RpaDrawerOverlayProps> = ({
  type,
  message,
  description,
  overlay = true,
  "data-testid": dataTestId,
}) => {
  const { colors } = useMantineTheme();

  const icons = {
    loading: <Loader color={colors.calm[5]} size={100} />,
    success: <IconV2.Success color={colors.calm[5]} size={{ height: 100 }} />,
    error: <IconV2.ErrorOutline color={colors.spark[5]} size={{ height: 100 }} />,
  };
  const icon = icons[type];

  return (
    <Flex.Column
      data-testid={dataTestId}
      justify="center"
      align="center"
      gap={8}
      px={16}
      py={32}
      sx={[
        (theme) => ({
          background: theme.colors.sky[5],
          width: "100%",
          height: "100%",
        }),
        optObj(overlay, {
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 100,
        }),
      ]}
    >
      <Flex mb={16} children={icon} />

      {message && (
        <RpiText
          type="p2"
          weight="regular"
          color="var(--codex-color-semantic-text-bold, #292929)"
          sx={{ textAlign: "center" }}
          children={message}
        />
      )}

      {description && (
        <RpiText
          type="error"
          weight="regular"
          color="var(--codex-color-semantic-text-subtle, #464646)"
          sx={{ textAlign: "center" }}
          children={description}
        />
      )}
    </Flex.Column>
  );
};
